import dayjs from 'dayjs';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Tooltip from 'components/global/Tooltip';

import useAlert from 'hooks/useAlert';
import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faDownload } from '@fortawesome/pro-light-svg-icons';

import { ExportFilterTypes, FilterType, convertFilterValueToDTO } from './DynamicListFilterUtils';
import { DynamicListComponentMap } from './DynamicListTable';
import { setDynamicListExporting } from './dynamicListSlice';

const DynamicListExportButton = ({
	columns,
	filterModel,
	sortModel,
	exportName,
	exportNameTranslationObject,
	fetchUrl
}) => {
	const axiosPrivate = useAxiosPrivate();
	const { t } = useTranslation();
	const { showError, showInfo } = useAlert();
	const { track } = useAppTracking();
	const { DateFormat, TimeZone } = useLocale();
	const dispatch = useDispatch();
	const isExporting = useSelector((state) => state.dynamicListData.isExporting);
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const exportHeaders = useMemo(() => {
		const tempColumns = structuredClone(columns.filter((col) => col?.show !== false));
		tempColumns.forEach((col, i) => {
			const { renderCell } = col;
			if (renderCell) {
				const exportFields = DynamicListComponentMap[renderCell].exportFields;
				if (exportFields) {
					exportFields.forEach((field) => {
						const isFound = tempColumns.some((col) => col.accessor === field);

						if (!isFound) {
							tempColumns.splice(i + 1, 0, {
								accessor: field,
								filterType: FilterType.string
							});
						}
					});
				}
			}
		});

		return tempColumns.map((col) => ({
			label: col.Header ? t(col.Header) : t(col.accessor),
			key: col.accessor,
			type: ExportFilterTypes[col.filterType] ?? FilterType.string
		}));
	}, [columns]);

	const handleExportData = useCallback(() => {
		const requestJson = {
			filter: filterModel.length > 0 ? filterModel.map((filter) => convertFilterValueToDTO(filter)) : undefined,
			sort: sortModel ? sortModel : undefined,
			export: {
				headers: exportHeaders,
				timezone: dayjs().tz(TimeZone).utcOffset(),
				dateFormat: DateFormat.dayjsFormat,
				name: exportName,
				translationObject: JSON.stringify(exportNameTranslationObject)
			}
		};

		axiosPrivate
			.post(fetchUrl, requestJson)
			.then((response) => {
				if (response.status !== 200) {
					showError();
				} else {
					showInfo({
						header: 'infoMsg.exportInProgressHeader',
						message: 'infoMsg.exportInProgressMessage'
					});
				}
				track('exportRequested', { name: exportName, records: exportHeaders.map((header) => header.label) }, [
					'mixpanel'
				]);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		dispatch(
			setDynamicListExporting({
				isExporting: true
			})
		);
	}, [filterModel, sortModel, exportName, exportHeaders, DateFormat, TimeZone, exportNameTranslationObject]);

	return (
		<Tooltip title="exportData" placement="top">
			<Button
				className="lord-icon-size"
				variant={isMobile ? 'action' : 'borderless'}
				icon={faDownload}
				iconSize="medium"
				onClick={handleExportData}
				loading={isExporting}
			/>
		</Tooltip>
	);
};

export default DynamicListExportButton;
